import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Alert, Typography } from '@mui/material';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute - Strictly enforces ADMIN role access
 * Only users with role === 'ADMIN' can access routes protected by this component
 * All other users (including contractors) are blocked and redirected
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography variant="body2">Loading authentication...</Typography>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const userRole = user.role?.toUpperCase();

  // STRICT: Only ADMIN role can access
  if (userRole !== 'ADMIN') {
    console.warn('[AdminRoute] Access denied - User role:', userRole, 'Required: ADMIN');
    
    if (userRole === 'CONTRACTOR') {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              ⛔ Access Denied - Administrator Portal Restricted
            </Typography>
            <Typography sx={{ mt: 1 }}>
              You are logged in as a <strong>Contractor</strong>, but this area is restricted to <strong>Administrators only</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Redirecting to your contractor portal...
            </Typography>
          </Alert>
          <Navigate to="/contractor/dashboard" replace />
        </Box>
      );
    }

    // Unknown role - redirect to login
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;

